import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { TrendingUp, Plus, Save, X, Edit3, Trash2, ChevronRight, Rocket, FolderPlus, Megaphone } from "lucide-react";

export default function Servizi() {
  const { isAdmin } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [editSlotForm, setEditSlotForm] = useState({ title: "" });
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ title: "" });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const slotsRes = await axios.get(`${API}/content/servizi-slots`);
      setSlots(slotsRes.data);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const addSlot = async () => {
    if (!newSlot.title.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    try {
      await axios.post(`${API}/content`, {
        section: "servizi-slots",
        content_type: "slot",
        title: newSlot.title,
        content: newSlot.title.toLowerCase().replace(/\s+/g, '-'),
        order: slots.length
      });
      toast.success("Categoria aggiunta");
      setNewSlot({ title: "" });
      setShowAddSlot(false);
      fetchContent();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const startEditSlot = (slot, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingSlotId(slot.id);
    setEditSlotForm({ title: slot.title });
  };

  const saveSlot = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.put(`${API}/content/${id}`, { title: editSlotForm.title });
      toast.success("Aggiornato");
      setEditingSlotId(null);
      fetchContent();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const deleteSlot = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Eliminato");
      fetchContent();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const getSlotIcon = (index) => {
    const icons = [TrendingUp, Rocket, Megaphone, FolderPlus];
    return icons[index % icons.length];
  };

  const getSlotColor = (index) => {
    const colors = ["#2FD8A8", "#F7931A", "#E1306C", "#00D4FF"];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center" data-testid="servizi-loading">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="page-container trading-bg" data-testid="servizi-page">
      <PageHero image={HERO_IMAGES.servizi} kicker="Formazione" title="Percorso e Servizi" subtitle="Seleziona una categoria per accedere ai contenuti formativi" />
      <SectionBg type="trading" />
      <div className="container py-8 relative z-10">

        {/* Admin Add Slot */}
        {isAdmin && (
          <div className="mb-10 text-center">
            {!showAddSlot ? (
              <button
                onClick={() => setShowAddSlot(true)}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <Plus size={18} /> Aggiungi Categoria
              </button>
            ) : (
              <div className="glass-card p-5 max-w-md mx-auto space-y-4">
                <h3 className="text-lg font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>
                  Nuova Categoria
                </h3>
                <input
                  type="text"
                  value={newSlot.title}
                  onChange={(e) => setNewSlot({ title: e.target.value })}
                  className="admin-input"
                  placeholder="Nome categoria"
                />
                <div className="flex gap-3">
                  <button onClick={addSlot} className="btn-primary flex-1 flex items-center justify-center gap-2">
                    <Save size={16} /> Salva
                  </button>
                  <button onClick={() => { setShowAddSlot(false); setNewSlot({ title: "" }); }} className="btn-secondary flex-1">
                    Annulla
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category Widgets Grid - 2 columns */}
        <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
          {slots.map((slot, index) => {
            const Icon = getSlotIcon(index);
            const color = getSlotColor(index);
            
            if (editingSlotId === slot.id) {
              return (
                <div 
                  key={slot.id}
                  className="glass-card p-4 sm:p-5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    value={editSlotForm.title}
                    onChange={(e) => setEditSlotForm({ title: e.target.value })}
                    className="admin-input mb-3 text-center"
                  />
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => saveSlot(slot.id, e)} 
                      className="flex-1 p-2 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "rgba(47, 216, 168, 0.2)", border: "1px solid var(--mm-gold)" }}
                    >
                      <Save size={18} style={{ color: "var(--mm-gold)" }} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setEditingSlotId(null); }}
                      className="flex-1 p-2 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "var(--mm-surface)", border: "1px solid var(--mm-border)" }}
                    >
                      <X size={18} style={{ color: "var(--mm-text-secondary)" }} />
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={slot.id}
                to={`/servizi/${slot.content}`}
                className="glass-card p-5 sm:p-6 md:p-8 flex flex-col items-center justify-center text-center group transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] relative aspect-square"
                style={{ 
                  borderColor: `${color}30`,
                  minHeight: "160px"
                }}
                data-testid={`slot-widget-${slot.content}`}
              >
                {/* Admin Edit/Delete Buttons */}
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button 
                      onClick={(e) => startEditSlot(slot, e)}
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: "rgba(47, 216, 168, 0.2)" }}
                    >
                      <Edit3 size={14} style={{ color: "var(--mm-gold)" }} />
                    </button>
                    <button 
                      onClick={(e) => deleteSlot(slot.id, e)}
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: "rgba(239, 68, 68, 0.2)" }}
                    >
                      <Trash2 size={14} style={{ color: "#EF4444" }} />
                    </button>
                  </div>
                )}

                {/* Icon */}
                <div 
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${color}20` }}
                >
                  <Icon size={28} style={{ color: color }} />
                </div>

                {/* Title */}
                <h2 
                  className="text-lg sm:text-xl md:text-2xl font-bold mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}
                >
                  {slot.title}
                </h2>

                {/* Arrow */}
                <div 
                  className="flex items-center gap-1 transition-all duration-300 group-hover:gap-2"
                  style={{ color: color }}
                >
                  <span className="text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ENTRA</span>
                  <ChevronRight size={16} />
                </div>

                {/* Hover glow */}
                <div 
                  className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ boxShadow: `inset 0 0 40px ${color}15` }}
                />
              </Link>
            );
          })}

          {/* Add new slot widget for admin */}
          {isAdmin && !showAddSlot && slots.length > 0 && (
            <button
              onClick={() => setShowAddSlot(true)}
              className="glass-card p-5 sm:p-6 md:p-8 flex flex-col items-center justify-center text-center transition-all duration-300 hover:scale-[1.03] aspect-square"
              style={{ 
                borderColor: "var(--mm-border)",
                borderStyle: "dashed",
                minHeight: "160px"
              }}
            >
              <Plus size={32} style={{ color: "var(--mm-gold)", marginBottom: "12px" }} />
              <span style={{ color: "var(--mm-text-secondary)", fontFamily: "'Space Grotesk', sans-serif" }}>
                Aggiungi
              </span>
            </button>
          )}
        </div>

        {slots.length === 0 && !isAdmin && (
          <div className="glass-card p-12 text-center max-w-md mx-auto">
            <TrendingUp size={48} style={{ color: "var(--mm-gold)", margin: "0 auto 16px" }} />
            <p style={{ color: "var(--mm-text-secondary)" }}>
              Le categorie verranno aggiunte a breve
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
